import React, { useState, useEffect, useRef, SyntheticEvent, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import './SendPage.css';
import SendPageHeader from './components/SendPageHeader';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import CardGroupContainerResponsive from 'components/CardGroupContainer/CardGroupContainerResponsive';
import InitiateTradeModal, { TradeCard } from './components/InitiateTradeModal';
import {
  useGetAvailablePackagesQuery,
  useInitiateTradeMutation,
  TradePackage,
  TradeCard as ApiTradeCard,
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  tradeApi,
  useGetMatchIndexQuery,
} from 'store/services/trade/tradeApi';
import { RootState } from 'store';
import { Points } from 'components/Points/Points';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import { useResendVerificationMutation } from 'store/services/auth/authApi';
import { getSetCode, isUUID } from 'utils/setMappings';
import { FoilCardImage } from 'components/FoilCardImage';
import SendCardTooltip from './components/SendCardTooltip';
import Pagination from 'components/Pagination/Pagination';

const PLACEHOLDER_IMAGE = AssetUrls.search.imageComing;

// Helper function to clean malformed URLs from backend
const cleanImageUrl = (url: string | null | undefined): string => {
  if (!url) return AssetUrls.profile.defaultAvatar;

  // If URL contains the error pattern, strip it
  if (url.includes('{failed')) {
    const cleanUrl = url.split('{failed')[0].trim();
    return cleanUrl || AssetUrls.profile.defaultAvatar;
  }

  return url;
};

export default function SendPage2() {
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TradePackage | null>(null);
  const [displayMode, setDisplayMode] = useState<'comfortable' | 'compact'>('comfortable');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [currentLockId, setCurrentLockId] = useState<string | null>(null);
  const [currentLockToken, setCurrentLockToken] = useState<string | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<string | null>(null);
  // Initialize search from URL params if present
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  // Pagination state
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Track user-initiated fetches (pagination/search) vs background polling
  const [isUserInitiatedFetch, setIsUserInitiatedFetch] = useState(false);
  // Track selected cards per package (packageUserId -> Set of cardIds)
  const [selectedCardsPerPackage, setSelectedCardsPerPackage] = useState<Map<string, Set<string>>>(new Map());
  const [removedPackages, setRemovedPackages] = useState<Set<string>>(new Set());
  const [loadingMessage, setLoadingMessage] = useState('Loading available packages...');
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimer2Ref = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Auto-Match State Management
  const [globalAutoMatch, setGlobalAutoMatch] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sendPage_globalAutoMatch') === 'true';
    } catch {
      return false;
    }
  });

  // Per-package auto-match states (session only - not persisted)
  const [packageAutoMatchStates, setPackageAutoMatchStates] = useState<Map<string, boolean>>(new Map());

  // Hover tooltip state
  const [hoveredCardData, setHoveredCardData] = useState<{
    cardId: string;
    position: { x: number; y: number };
    data: ApiTradeCard;
    username: string;
  } | null>(null);
  const [hoveredSetCode, setHoveredSetCode] = useState<string | null>(null);

  // Redux state for rate limiting and maintenance mode
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);

  // Calculate polling interval (pause if modal open, rate limited, or maintenance mode)
  const pollingInterval = (isModalOpen || isRateLimited || isMaintenanceMode) ? 0 : 15000;

  // Fetch available packages from the API
  const { data, isLoading, error, isFetching } = useGetAvailablePackagesQuery(
    {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: debouncedSearchTerm.trim() || undefined
    },
    {
      pollingInterval // Poll every 15 seconds (or pause if conditions met)
    }
  );

  // Fetch match index for auto-match feature
  const {
    data: matchIndexData,
    isLoading: isMatchIndexLoading,
    refetch: refetchMatchIndex,
  } = useGetMatchIndexQuery();

  // Debug authentication state on staging
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'staging') {
      console.log('🔐 Staging Auth Debug:', {
        isAuthenticated: !!localStorage.getItem('user'),
        hasCookies: document.cookie.length > 0,
        cookieList: document.cookie,
        apiUrl: process.env.REACT_APP_API_URL,
        cookieAuth: process.env.REACT_APP_USE_COOKIE_AUTH
      });

      if (error) {
        console.error('📦 Package fetch error:', error);
      }
    }
  }, [error]);

  const [initiateTrade, { isLoading: isInitiatingTrade }] = useInitiateTradeMutation();
  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [resendVerification] = useResendVerificationMutation();

  // Pre-fetch user data to keep session warm
  const { refetch: refetchUser } = useGetMeQuery();

  // We now use comprehensive static mappings from utils/setMappings.ts
  // No need to fetch from backend anymore

  // Only show loading state on initial load, not when refetching cached data
  const showLoadingState = isLoading && !data;

  // Show loading overlay only during user-initiated pagination/search, not background polling
  const showPaginationLoading = isFetching && !!data && isUserInitiatedFetch;

  // Timer to update loading message progressively
  useEffect(() => {
    if (showLoadingState) {
      // Reset message when loading starts
      setLoadingMessage('Loading available packages...');

      // Set first timer to change message after 4 seconds
      loadingTimerRef.current = setTimeout(() => {
        setLoadingMessage('Building initial cache to improve future performance...');
      }, 4000);

      // Set second timer to change message after 10 seconds
      loadingTimer2Ref.current = setTimeout(() => {
        setLoadingMessage('Almost done...');
      }, 10000);
    } else {
      // Clear timers when loading stops
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
        loadingTimer2Ref.current = null;
      }
      // Reset message for next load
      setLoadingMessage('Loading available packages...');
    }

    // Cleanup on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
      }
    };
  }, [showLoadingState])

  // Persist global auto-match state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sendPage_globalAutoMatch', String(globalAutoMatch));
    } catch (error) {
      console.warn('Failed to persist auto-match state:', error);
    }
  }, [globalAutoMatch]);

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearchTerm) {
        setIsUserInitiatedFetch(true); // Mark as user-initiated when search changes
      }
      setDebouncedSearchTerm(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Reset user-initiated fetch flag when fetching completes
  useEffect(() => {
    if (!isFetching && isUserInitiatedFetch) {
      setIsUserInitiatedFetch(false);
    }
  }, [isFetching, isUserInitiatedFetch]);

  // Auto-Match handler functions
  const handleGlobalAutoMatchToggle = (enabled: boolean) => {
    setGlobalAutoMatch(enabled);
  };

  const handlePackageAutoMatchToggle = (packageUserId: number, enabled: boolean) => {
    // Only allow when global is OFF
    if (globalAutoMatch) return;

    const packageKey = `pkg-${packageUserId}`;
    setPackageAutoMatchStates(prev => {
      const next = new Map(prev);
      next.set(packageKey, enabled);
      return next;
    });
  };

  // Get effective auto-match state for a package
  const getPackageAutoMatchState = (packageUserId: number): boolean => {
    if (globalAutoMatch) return true;
    const packageKey = `pkg-${packageUserId}`;
    return packageAutoMatchStates.get(packageKey) ?? false;
  };

  // Helper function for filtering cards by match index
  const filterPackageCards = (
    packageCards: ApiTradeCard[],
    matchingCardIds: string[]
  ): ApiTradeCard[] => {
    // Extract wantListIds from matching IDs (format: "{wantListId}-{haveCardId}")
    // We filter by wantListId only because the match-index contains have-card IDs
    // while available-packages contains want-card IDs - these are different for "Set: Any" entries
    const matchingWantListIds = new Set(
      matchingCardIds.map(id => id.split('-')[0])
    );
    return packageCards.filter(card =>
      matchingWantListIds.has(String(card.wantListId))
    );
  };

  // Calculate match summary for header display
  const matchSummary = React.useMemo(() => {
    if (!matchIndexData || matchIndexData.error) return undefined;

    return {
      matchingCards: matchIndexData.totalMatchingCards,
      totalCards: data?.packages?.reduce((sum, pkg) => sum + pkg.cards.length, 0) || 0,
      matchingPackages: matchIndexData.totalMatchingPackages,
      totalPackages: data?.packages?.length || 0,
    };
  }, [matchIndexData, data?.packages]);

  const handleSendPackage = async (tradePackage: TradePackage) => {
    // Filter cards to only include selected ones
    const packageKey = `pkg-${tradePackage.userId}`;
    const selectedCards = selectedCardsPerPackage.get(packageKey);

    // If no specific selection, default to all cards selected
    const isAllSelected = !selectedCards || selectedCards.size === 0;

    let filteredCards: ApiTradeCard[] = [];

    if (isAllSelected && !removedPackages.has(packageKey)) {
      // All cards are selected (default state)
      filteredCards = tradePackage.cards;
    } else if (removedPackages.has(packageKey)) {
      // All cards removed - shouldn't send anything
      toast.warning('Please select at least one card to send');
      return;
    } else {
      // Some cards are selected
      tradePackage.cards.forEach(card => {
        let selectedQuantity = 0;
        for (let i = 0; i < card.quantity; i++) {
          const cardKey = `${card.id}-${i}`;
          if (selectedCards?.has(cardKey)) {
            selectedQuantity++;
          }
        }
        if (selectedQuantity > 0) {
          filteredCards.push({ ...card, quantity: selectedQuantity });
        }
      });

      if (filteredCards.length === 0) {
        toast.warning('Please select at least one card to send');
        return;
      }
    }

    // Update the package with filtered cards
    const packageToSend = { ...tradePackage, cards: filteredCards };

    // Pre-flight check: Validate authentication before attempting lock
    try {
      console.log('🔐 Performing pre-flight authentication check...');
      const result = await refetchUser();
      if (result.error) {
        throw result.error;
      }
      console.log('✅ Authentication validated');

      // Check email verification status
      if (!result.data?.emailVerified) {
        console.log('❌ Email not verified');
        // Show toast with resend button
        toast.warning(
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Please verify your email address to send cards. Check your inbox for the verification link.</span>
            <button
              onClick={async () => {
                try {
                  if (!result.data?.email) {
                    toast.error('Unable to send verification email. Please try again.');
                    return;
                  }
                  await resendVerification({ email: result.data.email }).unwrap();
                  toast.success('Verification email sent! Please check your inbox.');
                } catch (error) {
                  toast.error('Failed to send verification email. Please try again.');
                }
              }}
              style={{
                padding: '4px 8px',
                background: '#7C3AED',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              Resend
            </button>
          </div>,
          {
            autoClose: false,
            closeButton: true
          }
        );
        return;
      }
    } catch (authError) {
      console.error('❌ Pre-flight authentication check failed:', authError);
      toast.error('Please refresh the page to restore your session');
      // Force refresh user data
      dispatch(meApi.util.invalidateTags(['User']));
      return;
    }

    // Retry logic for cookie authentication issues
    let retryCount = 0;
    const maxRetries = 2;
    const retryDelay = 500; // ms

    while (retryCount <= maxRetries) {
      try {
        // Log attempt for debugging
        if (retryCount > 0) {
          console.log(`🔄 Retry attempt ${retryCount} for trade lock creation`);
        }

        // Try to create a lock for this receiver
        const lockResult = await createTradeLock({ receiverId: tradePackage.userId }).unwrap();

        if (lockResult.success) {
          setCurrentLockId(lockResult.lockId || null);
          setCurrentLockToken(lockResult.lockToken || null);
          setLockExpiresAt(lockResult.expiresAt || null);
          setSelectedPackage(packageToSend); // Use the filtered package
          setIsModalOpen(true);
          return; // Success - exit function
        } else {
          // Someone else has locked this receiver
          toast.error(lockResult.message || 'Another sender is building a trade for this receiver. Please check back soon.');
          return; // Exit - no retry needed for business logic errors
        }
      } catch (error: any) {
        console.error(`Failed to create trade lock (attempt ${retryCount + 1}):`, error);
        console.error('Lock creation error details:', {
          status: error.status,
          message: error.data?.message,
          error: error.data?.error,
          details: error.data?.details,
          receiverId: tradePackage.userId,
          attempt: retryCount + 1
        });

        // Handle case where lock endpoint returns an error
        if (error.status === 423 || error.data?.message?.includes('already locked')) {
          toast.error('This package is temporarily unavailable. Another sender is currently building a trade for this receiver. Please try again in a few minutes.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 409 || error.data?.message?.includes('already have a lock')) {
          toast.error('You already have another package open for trading. Please complete or cancel your current trade first.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 401 || error.status === 403) {
          // Authentication issue - likely cookie not set after signup or expired
          console.error('Authentication failed when creating lock - cookie issue detected');

          if (retryCount < maxRetries) {
            // Wait and retry - cookie might need time to propagate
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue; // Retry the request
          } else {
            // All retries exhausted
            toast.error('Your session has expired. Please refresh the page to continue trading.');

            // Force a user data refetch to potentially refresh the session
            dispatch(meApi.util.invalidateTags(['User']));
            return;
          }
        } else if (error.status === 400) {
          // 400 can mean various issues - parse the backend message
          let errorMessage = 'Unable to reserve this package. ';

          const backendMessage = error.data?.message || error.data?.error || '';
          if (backendMessage.includes('already have a lock') || backendMessage.includes('existing lock')) {
            errorMessage = 'You already have another package open for trading. Please complete or cancel your current trade first.';
          } else if (backendMessage.includes('trade limit')) {
            errorMessage = 'The receiver has reached their maximum number of active trades. Please try again later.';
          } else if (backendMessage.includes('package') && backendMessage.includes('not found')) {
            errorMessage = 'This package is no longer available. It may have been traded or removed.';
          } else if (backendMessage) {
            errorMessage = backendMessage; // Use backend message if specific
          } else {
            errorMessage += 'The package may no longer be available.';
          }

          toast.error(errorMessage);
          return; // Exit - no retry for bad requests
        } else if (error.status === 500) {
          // Server error
          toast.error('The trade service is temporarily unavailable. Please try again in a moment.');
          return;
        } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
          // Network error
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            toast.error('Unable to connect to the trade server. Please check your internet connection.');
            return;
          }
        } else {
          // Other errors - retry might help
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            // Lock is required - do not proceed without it
            toast.error('Unable to reserve this package for trading. Please try again or contact support if the problem persists.');
            return;
          }
        }
      }
    }
  };

  const handleCloseModal = async () => {
    // Release the lock if we have one
    if (currentLockToken && selectedPackage) {
      try {
        await releaseTradeLock({
          receiverId: selectedPackage.userId,
          lockToken: currentLockToken
        }).unwrap();

        // Force refresh of available packages to show the unlocked package
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));
      } catch (error: any) {
        console.error('Failed to release trade lock:', error);
        // Silent failure - lock will expire automatically after 5 minutes
      }
    }

    // Clear state regardless of lock release success
    setIsModalOpen(false);
    setSelectedPackage(null);
    setCurrentLockId(null);
    setCurrentLockToken(null);
    setLockExpiresAt(null);
  };

  const handleInitiateTrade = async (cards: TradeCard[]) => {
    if (!selectedPackage) {
      toast.error('No package selected');
      return;
    }

    // Validate that no cards have 0 value
    const hasZeroValueCards = cards.some(card => {
      const cardValue = card.value || 0;
      return cardValue <= 0;
    });

    if (hasZeroValueCards) {
      toast.error('Cannot initiate trade: One or more cards have 0 points. Please remove cards with no value.');
      return;
    }

    // Calculate total value to ensure it's not 0
    const totalValue = cards.reduce((sum, card) => sum + (card.value || 0) * card.qty, 0);

    if (totalValue <= 0) {
      toast.error('Cannot initiate trade: Total trade value must be greater than 0 points.');
      return;
    }

    // All price validation happens server-side
    // Frontend prices are for display only

    try {
      // Map the cards from modal format to API format
      const apiCards = cards.map(card => {
        // Find the original card from the package to get the wantListId
        const originalCard = selectedPackage.cards.find(c => c.id === card.id);
        if (!originalCard) {
          throw new Error(`Card ${card.name} not found in package`);
        }

        // Debug logging for cardId tracking
        console.log(`Card ${card.name} - Set: ${card.set}`, {
          originalCardId: originalCard.cardId,
          modalCardId: (card as any).cardId,
          selectedSetCode: card.setCode,
          wantListId: originalCard.wantListId,
          acceptedPrintingIds: (originalCard as any).acceptedPrintingIds
        });

        // Use the cardId from the modal if it was updated (when user changed set)
        // Otherwise fall back to the original cardId
        // This ensures we use the specific card version if selected in the modal
        const finalCardId = card.cardId || originalCard.cardId;

        return {
          wantListId: originalCard.wantListId,
          cardId: finalCardId?.toString(),  // Include specific card ID for exact version matching
          quantity: card.qty,
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          edition: card.edition || undefined,
          set: card.setCode,  // Use setCode for API calls, not set name
        };
      });

      // Debug log the payload being sent
      console.log('Trade initiation payload:', {
        receiverId: selectedPackage.userId,
        cards: apiCards.map(c => ({ ...c, cardName: cards.find(card => card.id === selectedPackage.cards.find(oc => oc.wantListId === c.wantListId)?.id)?.name }))
      });

      const result = await initiateTrade({
        receiverId: selectedPackage.userId,
        lockToken: currentLockToken || undefined,
        cards: apiCards,
      }).unwrap();

      if (result.success) {
        toast.success('Trade initiated successfully!');

        // Clear lock state immediately - backend auto-releases lock on successful trade
        // This prevents handleCloseModal from attempting a redundant release
        setCurrentLockToken(null);
        setCurrentLockId(null);
        setLockExpiresAt(null);

        // Force refresh packages after successful trade
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));

        // Close modal and clear remaining state
        setIsModalOpen(false);
        setSelectedPackage(null);

        // Redirect to /sending page to see the new trade
        navigate('/sending');
      } else {
        toast.error(result.message || 'Failed to initiate trade');
      }
    } catch (error: any) {
      console.error('Failed to initiate trade:', error);
      console.error('Trade initiation error details:', {
        status: error.status,
        message: error.data?.message,
        error: error.data?.error,
        details: error.data?.details,
        cards: cards.map(c => ({ name: c.name, qty: c.qty, set: c.set })),
        receiverId: selectedPackage.userId
      });

      // Handle specific error cases from backend
      const errorMessage = error.data?.message || error.data?.error || error.message || '';

      // Parse error message for specific scenarios
      if (errorMessage.includes('Insufficient points')) {
        // Try to extract how many points are needed
        const pointsMatch = errorMessage.match(/needs? (\d+) (?:more )?points?/i);
        if (pointsMatch) {
          toast.error(`The receiver doesn't have enough points to complete this trade. They need ${pointsMatch[1]} more points.`);
        } else {
          toast.error('The receiver doesn\'t have enough points to complete this trade.');
        }
      } else if (errorMessage.includes('not found in want list') || errorMessage.includes('no longer wanted')) {
        // Extract card name if available
        const cardMatch = errorMessage.match(/card "([^"]+)"/i) || errorMessage.match(/(\w+) (?:is |are )?not found/i);
        if (cardMatch) {
          toast.error(`"${cardMatch[1]}" is no longer in the receiver's want list. The package has been updated.`);
        } else {
          toast.error('Some cards are no longer in the receiver\'s want list. The package has been updated - please try again.');
        }
      } else if (errorMessage.includes('quantity exceeds') || errorMessage.includes('too many copies')) {
        // Extract card and quantity info
        const quantityMatch = errorMessage.match(/only wants? (\d+) (?:copies?|cards?).*?(?:of |for )?["']?([^"']+)["']?/i);
        if (quantityMatch) {
          toast.error(`The receiver only wants ${quantityMatch[1]} copies of "${quantityMatch[2]}". Please reduce the quantity.`);
        } else if (errorMessage.includes('already sent') || errorMessage.includes('already received')) {
          // Handle case where user already sent some copies
          const sentMatch = errorMessage.match(/already (?:sent|received) (\d+).*?wants? (\d+)/i);
          if (sentMatch) {
            toast.error(`You've already sent ${sentMatch[1]} copies to this user. They only want ${sentMatch[2]} total copies.`);
          } else {
            toast.error('You\'ve already sent some copies of this card to this user. They don\'t need more.');
          }
        } else {
          toast.error('The quantity exceeds what the receiver wants. Please check their want list.');
        }
      } else if (errorMessage.includes('shipping address') || errorMessage.includes('address required')) {
        toast.error('You must add a shipping address in Settings before initiating trades.');
      } else if (errorMessage.includes('User not found') || errorMessage.includes('user does not exist')) {
        toast.error('Unable to verify user information. Please log out and log back in.');
      } else if (errorMessage.includes('lock expired') || errorMessage.includes('lock invalid') || errorMessage.includes('Valid lock required')) {
        toast.error('Your package reservation has expired (5 minute limit). Please select the package again.');
      } else if (errorMessage.includes('not in acceptable sets') || errorMessage.includes('set not accepted')) {
        // Extract set and card info
        const setMatch = errorMessage.match(/(?:card |)["']?([^"']+)["']?.*?from (?:the |)["']?([^"']+)["']? set/i);
        if (setMatch) {
          toast.error(`The receiver doesn't accept "${setMatch[1]}" from the "${setMatch[2]}" set. Please choose a different version.`);
        } else {
          toast.error('The selected card version is not accepted by the receiver. Please choose a different set.');
        }
      } else if (errorMessage.includes('condition not acceptable') || errorMessage.includes('better condition')) {
        // Extract condition requirements
        const conditionMatch = errorMessage.match(/requires? (?:at least |minimum |)(\w+)/i);
        if (conditionMatch) {
          toast.error(`The receiver requires better condition for this card. Minimum condition: ${conditionMatch[1]}.`);
        } else {
          toast.error('The receiver requires better condition for this card.');
        }
      } else if (errorMessage.includes('duplicate trade') || errorMessage.includes('already initiated')) {
        toast.error('You\'ve already initiated a similar trade with this user. Please wait for it to complete before sending more.');
      } else if (errorMessage.includes('max trades') || errorMessage.includes('trade limit')) {
        toast.error('You\'ve reached the maximum number of active trades. Please complete some trades before starting new ones.');
      } else if (error.status === 500) {
        toast.error('The trade service encountered an error. Please try again in a moment.');
      } else if (error.status === 503) {
        toast.error('The trade service is temporarily unavailable. Please try again later.');
      } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
        toast.error('Unable to connect to the trade server. Please check your internet connection.');
      } else if (errorMessage) {
        // Use backend error message if it's specific and not already handled
        toast.error(errorMessage);
      } else {
        // Generic fallback
        toast.error('Failed to initiate trade. Please try again or contact support if the problem persists.');
      }
    }
  };

  // Handle toggling individual card selection
  const handleToggleCard = (packageUserId: number, cardId: string) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);
    let newSelected: Set<string>;

    if (currentSelected === undefined) {
      // First interaction with this package
      if (isPackageRemoved) {
        // First click after Remove All - select only this card
        newSelected = new Set<string>([cardId]);
        // Remove from removed packages since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first click - select all cards except clicked one
        const pkg = data?.packages.find(p => p.userId === packageUserId);
        newSelected = new Set<string>();
        if (pkg) {
          pkg.cards.forEach(card => {
            for (let i = 0; i < card.quantity; i++) {
              const key = `${card.id}-${i}`;
              if (key !== cardId) { // Add all cards except the one being clicked
                newSelected.add(key);
              }
            }
          });
        }
      }
    } else {
      // Already interacted with package - just toggle the card
      newSelected = new Set(currentSelected);
      if (newSelected.has(cardId)) {
        newSelected.delete(cardId);
      } else {
        newSelected.add(cardId);
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle card hover for tooltip display
  const handleCardMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    card: ApiTradeCard,
    username: string
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Account for hover transform: scale(1.5) adds 25% to right side
    // transform-origin is top center, so top stays the same
    const scaledRightEdge = rect.right + (rect.width * 0.25);
    setHoveredCardData({
      cardId: card.id,
      position: { x: scaledRightEdge, y: rect.top },
      data: card,
      username,
    });
  };

  const handleCardMouseLeave = () => {
    setHoveredCardData(null);
    setHoveredSetCode(null);
  };

  // Calculate pagination values (search is now server-side)
  const totalPages = data?.totalPackages
    ? Math.ceil(data.totalPackages / ITEMS_PER_PAGE)
    : 1;

  // Page change handler
  const handlePageChange = useCallback((newPage: number) => {
    setIsUserInitiatedFetch(true); // Mark as user-initiated when page changes
    setCurrentPage(newPage);
    // Scroll the main content container (same pattern as ScrollToTop.tsx)
    const mainContent = document.querySelector('.main-page-content');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Handle toggling all instances of a card (for compact mode)
  const handleToggleAllCardInstances = (packageUserId: number, cardId: string, quantity: number) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);

    // Check current selection state of all instances
    let selectedCount = 0;
    if (isPackageRemoved) {
      selectedCount = 0;
    } else if (currentSelected === undefined) {
      // No interaction yet - default to all selected
      selectedCount = quantity;
    } else {
      // Count how many instances are selected
      for (let i = 0; i < quantity; i++) {
        const cardKey = `${cardId}-${i}`;
        if (currentSelected.has(cardKey)) {
          selectedCount++;
        }
      }
    }

    // Determine new selection state
    let newSelected: Set<string>;
    if (currentSelected === undefined) {
      // First interaction with this package
      newSelected = new Set<string>();

      if (isPackageRemoved) {
        // Package was removed (Remove All was clicked)
        // Only select the clicked card instances
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.add(cardKey);
        }
        // Clear removed packages flag since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first interaction - select all cards except the clicked one
        const pkg = data?.packages.find(p => p.userId === packageUserId);
        if (pkg) {
          // Add all cards to selection
          pkg.cards.forEach(card => {
            for (let i = 0; i < card.quantity; i++) {
              const key = `${card.id}-${i}`;
              newSelected.add(key);
            }
          });
        }
        // Now remove all instances of the clicked card
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.delete(cardKey);
        }
      }
    } else {
      newSelected = new Set(currentSelected);

      // Toggle: if all selected -> deselect all; otherwise -> select all
      if (selectedCount === quantity) {
        // All selected -> deselect all
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.delete(cardKey);
        }
      } else {
        // Not all selected -> select all
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.add(cardKey);
        }
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle removing all cards from a package
  const handleRemoveAllCards = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;
    const newMap = new Map(selectedCardsPerPackage);
    newMap.delete(packageKey); // Clear all selections for this package
    setSelectedCardsPerPackage(newMap);

    // Add to removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.add(packageKey);
    setRemovedPackages(newRemoved);
  };

  // Handle resetting a package (re-selecting all cards)
  const handleResetPackage = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;

    // Find the package to get all card IDs
    const pkg = data?.packages.find(p => p.userId === packageUserId);
    if (pkg) {
      const allCardIds = new Set<string>();
      pkg.cards.forEach(card => {
        for (let i = 0; i < card.quantity; i++) {
          allCardIds.add(`${card.id}-${i}`);
        }
      });

      const newMap = new Map(selectedCardsPerPackage);
      newMap.set(packageKey, allCardIds);
      setSelectedCardsPerPackage(newMap);
    }

    // Remove from removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.delete(packageKey);
    setRemovedPackages(newRemoved);
  };

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>, card: any) => {
    const img = e.currentTarget;
    const currentSrc = img.src;

    // Prevent infinite loops - check if we've already tried placeholder
    if (currentSrc.includes('image-coming-soon.png')) {
      img.onerror = null;
      return;
    }

    // First try backend-provided fallback URLs if available
    const fallbacks = card?.normal_fallbacks;
    if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
      img.dataset.fallbackAttempted = 'true';
      img.src = fallbacks[0];
      return;
    }

    // Try to extract Scryfall ID from CDN URL to attempt different formats
    const cdnMatch = currentSrc.match(/\/cards\/(normal|small|large)\/([a-f0-9-]+)\.jpg/);

    if (cdnMatch && !img.dataset.formatFallbackComplete) {
      const currentFormat = cdnMatch[1];
      const scryfallId = cdnMatch[2];

      // Try fallback formats: normal -> small -> large -> placeholder
      if (currentFormat === 'normal') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
        return;
      } else if (currentFormat === 'small') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
        return;
      } else if (currentFormat === 'large') {
        img.dataset.formatFallbackComplete = 'true';
      }
    }

    // If all fallbacks failed, use placeholder
    img.src = PLACEHOLDER_IMAGE;
    img.onerror = null;
    setFailedImages(prev => new Set(prev).add(card.id));
  };


  const getCurrentCards = (): TradeCard[] => {
    if (!selectedPackage) return [];

    // Convert API cards to TradeCard format for the modal
    return selectedPackage.cards.map(card => {
      // Check price availability
      const hasNonFoil = !!(card.nonFoilPointValue && card.nonFoilPointValue > 0);
      const hasFoil = !!(card.foilPointValue && card.foilPointValue > 0);

      // Respect receiver's explicit foil preferences
      const acceptsFoilOnly = card.acceptFoil && !card.acceptNonFoil;
      const acceptsNonFoilOnly = !card.acceptFoil && card.acceptNonFoil;

      // Use receiver's preference if explicit, otherwise default to non-foil when both available
      const defaultFoil = acceptsFoilOnly ? true :
                          acceptsNonFoilOnly ? false :
                          (hasNonFoil ? false : (hasFoil ? true : card.foil));

      // Default to best condition (NEAR_MINT)
      const defaultCondition = 'NEAR_MINT';

      // Calculate initial value based on defaults
      const initialValue = defaultFoil
        ? (card.foilPointValue || card.pointValue)
        : (card.nonFoilPointValue || card.pointValue);

      // Check if backend already provides acceptableSetDetails
      // If it does, use it directly (it may contain cardId and imageUrl for each set)
      let acceptableSetDetails = (card as any).acceptableSetDetails;

      // Only create our own acceptableSetDetails if backend doesn't provide it or it's empty
      // Trust the backend's acceptableSetDetails when it exists - it's the authoritative source
      if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
        acceptableSetDetails = card.acceptableSets?.map((setName, index) => {
          // Try to get code from backend's acceptableSetCodes array
          const backendCode = (card as any).acceptableSetCodes?.[index];

          if (backendCode) {
            // Backend provided the code
            return { name: setName, code: backendCode };
          }

          // No backend code, try our mapping
          const mappedCode = getSetCode(setName);
          if (mappedCode && mappedCode !== setName) {
            // We have a mapping that's different from the name
            return { name: setName, code: mappedCode };
          }

          // No mapping found - use the name as fallback
          return { name: setName, code: setName };
        });
      }

      // Determine the correct setCode to use
      let finalSetCode: string = '';

      // Check if backend's setCode is actually a UUID (backend bug)
      const isSetCodeUUID = card.setCode && isUUID(card.setCode);

      // If setCode is invalid or missing, find the correct code
      if (!card.setCode || isSetCodeUUID) {
        // Find the code from acceptableSetDetails for the current set
        if (acceptableSetDetails && acceptableSetDetails.length > 0) {
          const currentSetDetail = acceptableSetDetails.find((detail: any) => detail.name === card.set);
          if (currentSetDetail && currentSetDetail.code) {
            finalSetCode = currentSetDetail.code;
          } else {
            // Current set not in acceptable sets? Use the first acceptable set's code
            finalSetCode = acceptableSetDetails[0].code;
          }
        } else {
          // No acceptableSetDetails? Try our mapping
          const mapped = getSetCode(card.set);
          if (mapped && mapped !== card.set) {
            finalSetCode = mapped;
          } else {
            // Last resort - use the set name
            finalSetCode = card.set;
          }
        }
      } else {
        // Backend provided a valid setCode
        finalSetCode = card.setCode;
      }

      return {
        id: card.id,
        qty: card.quantity,
        maxQty: card.quantity,  // Use the wanted quantity from their want list as the max
        name: card.name,
        set: card.set,  // Current set name
        setCode: finalSetCode || card.set,  // Use determined setCode, fallback to set name
        condition: defaultCondition,  // Default to NM
        language: card.language,
        foil: defaultFoil,  // Default to non-foil if available
        edition: card.edition || '',
        image: card.imageUrl,
        imageSmall: card.imageUrl?.replace('/large/', '/small/').replace('/normal/', '/small/'),
        value: initialValue,  // Use calculated initial value
        baseValue: card.pointValue,
        foilValue: card.foilPointValue,
        nonFoilValue: card.nonFoilPointValue,
        acceptableSets: card.acceptableSets,  // Pass acceptable sets from backend (for backward compatibility)
        acceptableSetCodes: (card as any).acceptableSetCodes,  // Pass acceptable set codes for backward compatibility
        acceptableSetDetails: acceptableSetDetails,  // Pass set details with names and codes
        wantListId: card.wantListId,  // Include wantListId for price calculations
        availableInFoil: hasFoil,  // Pass foil availability
        availableInNonFoil: hasNonFoil,  // Pass non-foil availability
        acceptFoil: card.acceptFoil,  // Pass receiver's foil preference
        acceptNonFoil: card.acceptNonFoil,  // Pass receiver's non-foil preference
        acceptableConditions: card.acceptableConditions  // Pass acceptable conditions
      };
    });
  };

  // Loading state - only show on initial load, not when refetching
  if (showLoadingState) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
        />
        <CardPrintingsContainer>
          <div className="send-loading-container">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {loadingMessage}
            </p>
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
        />
        <CardPrintingsContainer>
          <div style={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
            Error loading packages. Please try again later.
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // Filter out packages from users without valid shipping addresses, locked packages, AND packages with 0 value
  const packagesWithValidAddresses = data?.packages?.filter(pkg => {
    // Safety check - make sure pkg exists
    if (!pkg) return false;

    // Don't filter out the package we currently have locked and are working with
    if (pkg.isLocked && currentLockToken && selectedPackage && selectedPackage.userId === pkg.userId) {
      return true; // Keep our own locked package visible
    }

    // Skip if package is locked by another user
    if (pkg.isLocked) {
      return false;
    }

    const addr = pkg.shippingAddress;
    // Check if all required address fields are present and non-empty
    return addr &&
           addr.firstName &&
           addr.lastName &&
           addr.address1 &&
           addr.city &&
           addr.state &&
           addr.postalCode &&
           addr.country;
  }) || [];

  // SECURITY WARNING: This is for DISPLAY ONLY
  // Backend must recalculate all prices to prevent manipulation
  const getCardDisplayPrice = (card: ApiTradeCard): number => {
    // Backend calculates pointValue based on foil status - just use it directly
    return card.pointValue;
  };

  // SECURITY WARNING: This total is for DISPLAY ONLY
  // Backend must recalculate to prevent users from manipulating totals
  const calculatePackageTotalValue = (cards: ApiTradeCard[]): number => {
    // WARNING: This can be manipulated in DevTools
    // Backend will calculate real total during trade initiation
    return cards.reduce((sum, card) => {
      const displayPrice = getCardDisplayPrice(card);
      return sum + (displayPrice * card.quantity);
    }, 0);
  };

  return (
    <div className="send-page">
      <h1 className="send-page-title">Send Cards</h1>
      <SendPageHeader
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        onSearch={setSearchQuery}
        initialSearchValue={searchQuery}
        globalAutoMatch={globalAutoMatch}
        onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
        isMatchIndexLoading={isMatchIndexLoading}
        hasMatchIndexError={!!matchIndexData?.error}
        onRetryMatchIndex={refetchMatchIndex}
        matchSummary={matchSummary}
      />

      <CardPrintingsContainer>
        {/* Loading overlay for pagination/search transitions */}
        {showPaginationLoading && (
          <div className="send-pagination-loading-overlay">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {debouncedSearchTerm ? `Searching for "${debouncedSearchTerm}"...` : `Loading page ${currentPage}...`}
            </p>
          </div>
        )}
        {packagesWithValidAddresses.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {searchQuery ? (
              <>
                <p style={{ margin: 0 }}>No packages found matching "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #666',
                    borderRadius: '4px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  Clear search filter
                </button>
              </>
            ) : (
              'No packages available at the moment. Check back later!'
            )}
          </div>
        ) : (
          <>
          {packagesWithValidAddresses.map((pkg) => {
          // Filter out cards with 0 value
          let valuedCards = pkg.cards.filter(card => {
            const cardValue = Math.max(
              card.pointValue || 0,
              card.foilPointValue || 0,
              card.nonFoilPointValue || 0
            );
            if (cardValue <= 0) {
              return false;
            }
            return true;
          });

          const packageKey = `pkg-${pkg.userId}`;
          const isPackageRemoved = removedPackages.has(packageKey);
          const isAutoMatchActive = getPackageAutoMatchState(pkg.userId);
          const packageMatch = matchIndexData?.packages?.[String(pkg.userId)];
          const totalCardsBeforeAutoMatch = valuedCards.length;

          // Apply auto-match filter if active and we have match data (or data loaded with no matches)
          // If matchIndexData exists but packageMatch is undefined, it means zero matches for this user
          const hasMatchIndexData = matchIndexData && !matchIndexData.error;
          if (isAutoMatchActive && hasMatchIndexData) {
            const matchingIds = packageMatch?.matchingCardIds ?? [];
            valuedCards = filterPackageCards(valuedCards, matchingIds);
          }

          const matchingCardCount = packageMatch?.matchingCardIds.length ?? 0;
          const totalCardCount = packageMatch?.totalCards ?? totalCardsBeforeAutoMatch;

          // Check if this is an empty state due to auto-match
          // Either: packageMatch exists with 0 matches, OR packageMatch is undefined (user not in index = 0 matches)
          const isEmptyDueToAutoMatch = isAutoMatchActive && hasMatchIndexData && valuedCards.length === 0 && totalCardsBeforeAutoMatch > 0;

          // When GLOBAL auto-match is on and no cards match, hide the package entirely
          if (globalAutoMatch && isEmptyDueToAutoMatch) {
            return null;
          }

          // Skip rendering the package if no cards have value (but NOT if it's empty due to individual auto-match)
          if (valuedCards.length === 0 && !isEmptyDueToAutoMatch) {
            return null;
          }

          return (
            <CardGroupContainerResponsive
              key={pkg.userId}
              title={isAutoMatchActive
                ? `${matchingCardCount} of ${totalCardCount} cards match`
                : `${valuedCards.reduce((sum, card) => sum + card.quantity, 0)} Cards in package`}
              userName={pkg.username}
              userAvatar={cleanImageUrl(pkg.avatarUrl)}
              userCountry={pkg.shippingAddress?.country}
              userPoints={pkg.pointBalance}
              totalValue={calculatePackageTotalValue(valuedCards)}
              autoMatch={isAutoMatchActive}
              mode="send"
              onAutoMatchToggle={(enabled) => handlePackageAutoMatchToggle(pkg.userId, enabled)}
              autoMatchDisabled={globalAutoMatch}
              matchingCardCount={matchingCardCount}
              totalCardCount={totalCardCount}
              onSend={() => handleSendPackage({...pkg, cards: valuedCards})}
              onRemoveAll={() => handleRemoveAllCards(pkg.userId)}
              onReset={() => handleResetPackage(pkg.userId)}
              isRemoved={isPackageRemoved}
            >
              {/* Empty state when auto-match filters out all cards */}
              {isEmptyDueToAutoMatch ? (
                <div className="auto-match-empty-state">
                  <p className="auto-match-empty-text">
                    No cards from your Have List match this trader's wants
                  </p>
                  <button
                    className="auto-match-view-all-btn"
                    onClick={() => handlePackageAutoMatchToggle(pkg.userId, false)}
                    disabled={globalAutoMatch}
                  >
                    {globalAutoMatch ? 'Turn off global Auto-Match to view all' : 'View all cards'}
                  </button>
                </div>
              ) : (
              <div className="card-grid">
                {displayMode === 'comfortable' ? (
                  // Comfortable mode: Show individual card images for each quantity
                  [...valuedCards].sort((a, b) => {
                  // Sort by the appropriate price (prefer nonfoil if available)
                  const aPrice = getCardDisplayPrice(a);
                  const bPrice = getCardDisplayPrice(b);
                  return bPrice - aPrice;
                }).flatMap((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  return Array.from({ length: card.quantity }, (_, index) => {
                    const cardKey = `${card.id}-${index}`;
                    const selectedCards = selectedCardsPerPackage.get(packageKey);

                    // Determine if card is selected
                    let isSelected = true;

                    if (isPackageRemoved) {
                      // If package is removed, all cards are deselected
                      isSelected = false;
                    } else if (selectedCards !== undefined) {
                      // If we have interacted with this package, use the selection set
                      // (even if it's empty - empty means nothing selected)
                      isSelected = selectedCards.has(cardKey);
                    }
                    // Otherwise, no interaction yet - default to all selected (true)

                    return (
                      <div
                        key={cardKey}
                        className="card-item"
                        onClick={() => handleToggleCard(pkg.userId, cardKey)}
                        onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                        onMouseLeave={handleCardMouseLeave}
                        style={{ cursor: 'pointer', position: 'relative' }}
                      >
                        <FoilCardImage
                          className="card-item-image"
                          src={card.imageUrl || PLACEHOLDER_IMAGE}
                          alt={card.name}
                          foil={card.foil}
                          borderRadius="8px"
                          onError={(e) => handleImageError(e, card)}
                          loading="lazy"
                        />
                        {(failedImages.has(card.id) || !card.imageUrl) && (
                          <div className="card-name-overlay">{card.name}</div>
                        )}
                        <div className="card-item-points">
                          <Points points={displayPrice} type="small" />
                        </div>
                        {!isSelected && (
                          <div className="deselected-overlay">
                            <img
                              className="heart-icon broken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                              alt="Deselected"
                            />
                            <img
                              className="heart-icon unbroken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/heart.svg"
                              alt="Click to select"
                            />
                          </div>
                        )}
                      </div>
                    );
                  });
                })
              ) : (
                // Compact mode: Show one card image with quantity badge
                [...valuedCards].sort((a, b) => {
                  // Sort by the appropriate price (prefer nonfoil if available)
                  const aPrice = getCardDisplayPrice(a);
                  const bPrice = getCardDisplayPrice(b);
                  return bPrice - aPrice;
                }).map((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  // Check if all instances of this card are deselected
                  const selectedCards = selectedCardsPerPackage.get(packageKey);
                  let selectedCount = 0;

                  if (isPackageRemoved) {
                    // All cards are deselected
                    selectedCount = 0;
                  } else if (selectedCards === undefined) {
                    // No interaction yet - default to all cards selected
                    selectedCount = card.quantity;
                  } else {
                    // We have interacted - count selected cards (even if set is empty)
                    for (let i = 0; i < card.quantity; i++) {
                      const cardKey = `${card.id}-${i}`;
                      if (selectedCards.has(cardKey)) {
                        selectedCount++;
                      }
                    }
                  }
                  const isFullyDeselected = selectedCount === 0;

                  return (
                    <div
                      key={card.id}
                      className="card-item"
                      onClick={() => handleToggleAllCardInstances(pkg.userId, card.id, card.quantity)}
                      onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                      onMouseLeave={handleCardMouseLeave}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      <div className="card-quantity-badge">{selectedCount > 0 ? selectedCount : card.quantity}</div>
                      <FoilCardImage
                        className="card-item-image"
                        src={card.imageUrl || PLACEHOLDER_IMAGE}
                        alt={card.name}
                        foil={card.foil}
                        borderRadius="8px"
                        onError={(e) => handleImageError(e, card)}
                        loading="lazy"
                      />
                      {(failedImages.has(card.id) || !card.imageUrl) && (
                        <div className="card-name-overlay">{card.name}</div>
                      )}
                      <div className="card-item-points">
                        <Points points={displayPrice} type="small" />
                      </div>
                      {isFullyDeselected && (
                        <div className="deselected-overlay" style={{ opacity: 0.7 }}>
                          <img
                            className="heart-icon"
                            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                            alt="All deselected"
                            style={{ width: 30, height: 30 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
              )}
          </CardGroupContainerResponsive>
          );
        })}
        {/* Global auto-match empty state when all packages are filtered out */}
        {globalAutoMatch && matchSummary?.matchingPackages === 0 && (
          <div className="auto-match-global-empty-state">
            <p>No matching packages found.</p>
            <p>Turn off Auto-Match to see all available trades.</p>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="send-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
              <span className="pagination-total">
                {' '}({data?.totalPackages || 0} users)
              </span>
            </div>
          </div>
        )}
          </>
        )}
      </CardPrintingsContainer>

      <InitiateTradeModal
        isOpen={isModalOpen}
        cards={getCurrentCards()}
        onClose={handleCloseModal}
        onInitiateTrade={handleInitiateTrade}
        isLoading={isInitiatingTrade}
        lockId={currentLockId || undefined}
        lockExpiresAt={lockExpiresAt || undefined}
      />

      {/* Card hover tooltip */}
      {hoveredCardData && (
        <SendCardTooltip
          cardData={hoveredCardData.data}
          position={hoveredCardData.position}
          username={hoveredCardData.username}
          onClose={handleCardMouseLeave}
          onSetHover={setHoveredSetCode}
          hoveredSetCode={hoveredSetCode}
        />
      )}
    </div>
  );
}
